'use client';

import { chatSidebarOpenAtom } from '@/atoms/chatSidebarAtoms';
import { pendingNavHrefAtom } from '@/atoms/sideNavAtoms';
import { sidebarPanelPinnedAtom } from '@/atoms/sidebarPanelAtoms';
import { ExternalLink } from '@/components/common/external-link';
import { IS_SIDEBAR_PANEL_ENABLED } from '@/components/constants/flags';
import {
  AGENT_HOME_NAV_ROW,
  useIsAgentHomeNav,
} from '@/components/layout/Navigation/nav-v3/agent-home-nav-context';
import { isNavItemAvailableOffline } from '@/components/offline-recording/offline-nav-availability';
import { useOfflineGating } from '@/hooks/desktop/useOfflineGating';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useIsMobile } from '@/hooks/useWindowDimensions';
import { cn } from '@/lib/cn';
import {
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@heidi/design-system/page-shell';
import { typographyVariant } from '@heidi/design-system/typography';
import { useAtomValue, useSetAtom, useStore } from 'jotai';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { forwardRef } from 'react';
import { useMobilePanel } from '../mobile/use-mobile-panel';
import { useSidebarPanelNav } from '../sidebar-panel';
import { NavItemCollapsingAnimation } from './nav-item-collapsing-animation';
import { NavItemLabelContent } from './nav-item-label-content';
import { createNavItemLinkClickHandler } from './nav-item-link-click';
import { navigateWithPendingCover } from './nav-item-navigation';
import { NavItemOfflineGated } from './nav-item-offline-gated';
import { NavItemMenuTrailing, NavItemRowContext } from './nav-item-row-context';
import type { NavItemProps } from './nav-item.types';
import { menuItemMarkerAttributes, routeMatches, routePath } from './nav-item.utils';

/**
 * Unified navigation item component for the sidebar.
 *
 * Consolidates functionality from NavV2Button and NavV2Link
 * while using PageShell styling patterns (SidebarMenuButton, SidebarMenuAction, etc.)
 *
 * Features:
 * - Automatic tooltip support when sidebar is collapsed
 * - Link or button mode based on href prop
 * - External link support
 * - Panel toggle chevron (SidebarMenuAction)
 * - Badge support (SidebarMenuBadge)
 * - Inline badge support (e.g., LibraryTag)
 * - Active state styling
 * - Highlight animation for tutorials
 *
 * @example
 * // Simple link
 * <NavItem
 *   label="Templates"
 *   icon={<Blocks />}
 *   href="/templates"
 * />
 *
 * @example
 * // Button with panel toggle
 * <NavItem
 *   label="Scribe"
 *   icon={<AudioLines />}
 *   onClick={handleScribeClick}
 *   panelToggle={{
 *     isOpen: isSessionsPanelOpen,
 *     onToggle: toggleSessionsPanel,
 *   }}
 * />
 *
 * @example
 * // Link with badge
 * <NavItem
 *   label="Insights"
 *   icon={<BarChart3 />}
 *   href="/insights"
 *   badge={<Badge />}
 * />
 */
export const NavItem = forwardRef<HTMLLIElement, NavItemProps>(function NavItem(
  {
    label,
    labelContent,
    icon,
    href,
    onClick,
    isActive,
    pinActive = false,
    activeMatch = 'prefix',
    activeHrefs,
    external = false,
    disabled = false,
    variant,
    layout = 'default',
    testId,
    ariaLabel,
    ariaExpanded,
    className,
    truncateLabel = false,
    itemClassName,
    menuItemDataAttributes,
    isHighlighted = false,
    panelToggle,
    badge,
    inlineBadge,
    mobilePanelContent,
    mobilePanelAction,
    action,
    rowContextMenu,
    belowContent,
    sidebarPanelType,
    onMouseEnter,
    onMouseLeave,
  },
  ref
) {
  const router = useRouter();
  const navStore = useStore();
  const isMobile = useIsMobile();
  const { openPanel } = useMobilePanel();
  const { state: sidebarState } = useSidebar();
  const isSidebarCollapsed = sidebarState === 'collapsed';
  // APP-6672: while offline, every connectivity-backed sidebar destination is
  // disabled — only the recording entry points stay live (see
  // {@link OFFLINE_AVAILABLE_NAV_TEST_IDS}). `shouldGateControls` no-ops on web /
  // online / prod, so online behaviour is unchanged.
  const { shouldGateControls } = useOfflineGating();
  const isOfflineGated = shouldGateControls && !isNavItemAvailableOffline(testId);
  // Sidebar panel hover handlers (lightweight hook — only subscribes to content atom)
  const {
    handleNavItemMouseEnter,
    handleNavItemMouseLeave,
    content: panelContent,
  } = useSidebarPanelNav();
  const setChatSidebarOpen = useSetAtom(chatSidebarOpenAtom);
  const setPanelPinned = useSetAtom(sidebarPanelPinnedAtom);
  const pendingNavHref = useAtomValue(pendingNavHrefAtom);
  const setPendingNavHref = useSetAtom(pendingNavHrefAtom);
  // Nav-v3 is single-level navigation — the hover trays never mount there, so
  // the hover machinery must not react either.
  const isAgentHomeNav = useIsAgentHomeNav();
  const navV3ClassName = cn(
    // Nav-v3 uses the standard secondary-body treatment used by the prior
    // in-page navigation rather than the compact 12px sidebar caption.
    isAgentHomeNav && typographyVariant({ variant: 'p2' }),
    isAgentHomeNav && AGENT_HOME_NAV_ROW,
    isAgentHomeNav && isSidebarCollapsed && '[&>svg]:size-4.5',
    className
  );
  const shouldShowSidebarPanel =
    IS_SIDEBAR_PANEL_ENABLED() && !isAgentHomeNav && !isMobile && !!sidebarPanelType;

  // Only extend the hover hitbox for the nav item currently showing in the panel.
  // This prevents other items' ::after zones from triggering content switches
  // without the nav item appearing visually hovered.
  const isActiveForPanel = shouldShowSidebarPanel && panelContent === sidebarPanelType;

  // Detect hover capability for touch device fallback (opens panel on click instead of hover)
  // Hook must be called unconditionally, but result is only used when panel feature is enabled
  const canHover = useMediaQuery('(hover: hover)', true);
  const shouldOpenPanelOnClick = shouldShowSidebarPanel && !canHover;

  // When sidebar is collapsed and item has panelToggle, clicking should toggle the panel
  const shouldTogglePanelOnClick = isSidebarCollapsed && !!panelToggle && !isMobile;
  const isIconOnly = layout === 'iconOnly';

  // `iconOnly` drops the visible label (see `NavItemProps.layout`), so it has to
  // come through as the accessible name — otherwise the button ships nameless.
  const resolvedAriaLabel = ariaLabel ?? (isIconOnly ? label : undefined);

  const buttonContent = (
    <>
      {icon}
      {!isIconOnly && (
        <NavItemLabelContent
          label={label}
          labelContent={labelContent}
          inlineBadge={inlineBadge}
          truncateLabel={truncateLabel}
        />
      )}
    </>
  );

  // Determine if we're rendering as a link or button
  // When collapsed with panelToggle, render as button to toggle the panel
  const isLink = !!href && !external && !shouldTogglePanelOnClick;
  const isExternalLink = !!href && external && !shouldTogglePanelOnClick;
  const currentHref = routePath(pendingNavHref ?? router.asPath);
  const targetHref = href ? routePath(href) : undefined;
  // `activeHrefs` can select a button-mode item (no href — e.g. Scribe in
  // mobile / desktop-legacy mode), so it must infer independently of targetHref
  // or those rows can't clear optimistically.
  const canInferActive = !external && (!!targetHref || !!activeHrefs?.length);
  const inferredIsActive =
    canInferActive &&
    ((!!targetHref && routeMatches(currentHref, targetHref, activeMatch)) ||
      activeHrefs?.some((activeHref) =>
        routeMatches(currentHref, routePath(activeHref), 'prefix')
      ) === true);
  // The pendingNavHref/canInferActive bypass exists so a row with NO caller
  // opinion shows the optimistic target immediately, and several existing
  // callers (e.g. Scribe's "steady-state only" isActive) deliberately rely on
  // it overriding their explicit isActive while THEIR OWN navigation is
  // pending. `pinActive` is the caller's opt-in for the opposite case: an
  // isActive that can be true for a reason unrelated to routing (e.g. Home
  // staying highlighted while its hover preview is open), which an unrelated
  // pending navigation elsewhere in the sidebar must never override.
  const resolvedIsActive =
    isActive !== undefined && pinActive
      ? isActive
      : pendingNavHref !== null && canInferActive
        ? inferredIsActive
        : (isActive ?? inferredIsActive);

  if (isOfflineGated) {
    return (
      <NavItemOfflineGated
        ref={ref}
        itemClassName={itemClassName}
        menuItemDataAttributes={menuItemDataAttributes}
        variant={variant}
        layout={layout}
        isIconOnly={isIconOnly}
        label={label}
        testId={testId}
        resolvedAriaLabel={resolvedAriaLabel}
        ariaExpanded={ariaExpanded}
        navV3ClassName={navV3ClassName}
        buttonContent={buttonContent}
      />
    );
  }

  const menuItemInner = (
    <>
      {isLink ? (
        // Internal link using Next.js Link
        <SidebarMenuButton
          asChild
          variant={variant}
          layout={layout}
          tooltip={label}
          isActive={resolvedIsActive}
          className={cn(isHighlighted && 'animation-ping', navV3ClassName)}
        >
          <Link
            href={href}
            // Disable viewport prefetch: every sidebar destination otherwise
            // prefetches its route chunk + data on mount, firing a wave of
            // requests on every page load for pages the clinician may never
            // visit this session. Next still prefetches on hover/touch, so
            // deliberate navigation stays fast. See ARC-219.
            prefetch={false}
            onClick={createNavItemLinkClickHandler({
              href,
              onClick,
              sidebarPanelType,
              setChatSidebarOpen,
              router,
              setPendingNavHref,
              testId,
              shouldOpenPanelOnClick,
              setPanelPinned,
            })}
            onNavigate={(event) =>
              navigateWithPendingCover(event, href, router, navStore)
            }
            data-testid={testId}
            aria-label={resolvedAriaLabel}
            aria-expanded={ariaExpanded}
            aria-disabled={disabled}
          >
            {buttonContent}
          </Link>
        </SidebarMenuButton>
      ) : isExternalLink ? (
        // External link
        <SidebarMenuButton
          asChild
          variant={variant}
          layout={layout}
          tooltip={label}
          isActive={resolvedIsActive}
          className={cn(isHighlighted && 'animation-ping', navV3ClassName)}
        >
          <ExternalLink
            href={href}
            onClick={onClick}
            data-testid={testId}
            aria-label={ariaLabel || label}
            aria-expanded={ariaExpanded}
            disabled={disabled}
          >
            {buttonContent}
          </ExternalLink>
        </SidebarMenuButton>
      ) : (
        // Button (no href, or collapsed with panel toggle)
        <SidebarMenuButton
          variant={variant}
          layout={layout}
          tooltip={label}
          isActive={resolvedIsActive}
          onClick={shouldTogglePanelOnClick ? () => panelToggle?.onToggle() : onClick}
          disabled={disabled}
          data-testid={testId}
          aria-label={resolvedAriaLabel}
          aria-expanded={ariaExpanded}
          className={navV3ClassName}
          // Inline style for animation to override SidebarMenuButton's transitions
          // which cause flickering when using the animation-ping class
          style={isHighlighted ? { animation: 'custom-ping 2s infinite' } : undefined}
        >
          {buttonContent}
        </SidebarMenuButton>
      )}

      <NavItemMenuTrailing
        panelToggle={panelToggle}
        isMobile={isMobile}
        mobilePanelContent={mobilePanelContent}
        mobilePanelAction={mobilePanelAction}
        openPanel={openPanel}
        label={label}
        badge={badge}
        action={action}
        belowContent={belowContent}
      />
    </>
  );

  const menuItem = (
    <SidebarMenuItem
      ref={ref}
      {...menuItemDataAttributes}
      {...menuItemMarkerAttributes({ shouldShowSidebarPanel, href })}
      data-panel-open={panelToggle?.isOpen || undefined}
      className={cn(
        isActiveForPanel &&
          // Extend hover hitbox to the right edge of the sidebar, bridging the
          // SidebarGroup padding gap. This prevents the panel from hiding when the
          // cursor moves rightward from the nav item toward the panel.
          'after:absolute after:inset-y-0 after:-right-2 after:w-2 after:content-[""]',
        itemClassName
      )}
      onMouseEnter={
        shouldShowSidebarPanel || onMouseEnter
          ? () => {
              onMouseEnter?.();
              if (shouldShowSidebarPanel) {
                handleNavItemMouseEnter(sidebarPanelType);
              }
            }
          : undefined
      }
      onMouseLeave={
        shouldShowSidebarPanel || onMouseLeave
          ? () => {
              onMouseLeave?.();
              if (shouldShowSidebarPanel) {
                handleNavItemMouseLeave();
              }
            }
          : undefined
      }
    >
      <NavItemCollapsingAnimation>{menuItemInner}</NavItemCollapsingAnimation>
    </SidebarMenuItem>
  );
  return <NavItemRowContext menu={rowContextMenu}>{menuItem}</NavItemRowContext>;
});
NavItem.displayName = 'NavItem';

export default NavItem;
